import { Box, Button, useToast } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { ApiMessageStack, Message, TableV2 } from "components";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { IntegrationEntitySyncStatusEnum } from "enums/integration-entity-sync-status.enum";
import { AlertStatusEnum } from "enums/alert-status.enum";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { keyBy } from "lodash";
import { UserAccountStore } from "store/UserAccounts/UserAccount/UserAccount.store";
import { syncAltoIntentionTableColumnDef } from "./syncAltoIntentionTableColumnDef";
import { TAltoIntentionRelationExtendedData } from "types/alto.type";
import { IntegrationConfiguratorLayout } from "../../IntegrationConfiguratorLayout";
import { SyncStatusFilter } from "../../SyncStatusFilter";

const DEFAULT_PAGE_SIZE = 10;

interface IProps {
  accountStore: UserAccountStore;
}

export const AltoIntegrationsIntentionsConfigurator: FC<IProps> = observer(
  ({ accountStore }) => {
    const toast = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newRelations, setNewRelations] = useState<
      Record<string, TAltoIntentionRelationExtendedData>
    >({});

    const accountIntegrationsStore = accountStore.accountIntegrationsStore;
    const accountIntegrationsAltoStore =
      accountIntegrationsStore.accountIntegrationAltoStore;
    const accountIntegrationAltoIntentionsStore =
      accountIntegrationsAltoStore.accountIntegrationAltoIntentionsStore;
    const {
      getSyncStatusFilter: syncStatusFilter,
      setSyncStatusFilter,
      filteredAltoSyncIntentionsData,
      altoSyncIntentionsData,
    } = accountIntegrationAltoIntentionsStore;

    const updateSyncPair = useCallback(
      (syncPair: TAltoIntentionRelationExtendedData) => {
        setNewRelations({
          ...newRelations,
          [syncPair.type]: syncPair,
        });
      },
      [setNewRelations, newRelations]
    );

    useEffect(() => {
      const suggestedArray = altoSyncIntentionsData.filter(
        syncData =>
          syncData.syncStatus === IntegrationEntitySyncStatusEnum.SUGGESTED_SYNC
      );
      const suggestedMap = keyBy<TAltoIntentionRelationExtendedData>(
        suggestedArray,
        syncData => syncData.type
      );
      setNewRelations({
        ...suggestedMap,
      });
    }, [altoSyncIntentionsData]);

    const columnsDef = useMemo(() => {
      return syncAltoIntentionTableColumnDef(accountStore, updateSyncPair);
    }, [accountStore, updateSyncPair]);

    const dataSource = useMemo(() => {
      return filteredAltoSyncIntentionsData.map(syncData => {
        if (!!newRelations[syncData.type]) return newRelations[syncData.type];

        return syncData;
      });
    }, [filteredAltoSyncIntentionsData, newRelations]);

    const submitSyncPairs = useCallback(async () => {
      try {
        setIsSubmitting(true);
        await accountIntegrationAltoIntentionsStore.updateAccountAltoSyncedIntentions(
          Object.values(newRelations)
        );
        toast({
          ...DEFAULT_SUCCESS_TOAST_OPTIONS,
          description: (
            <ApiMessageStack
              messageStack={
                "Alto integration lead sources configuration updated."
              }
            />
          ),
        });
      } catch (error) {
        toast({
          ...DEFAULT_ERROR_TOAST_OPTIONS,
          description: <ApiMessageStack messageStack={error.message} />,
        });
      } finally {
        setIsSubmitting(false);
      }
    }, [newRelations, accountIntegrationAltoIntentionsStore, toast]);

    const banner = useMemo(() => {
      const notSyncedArray = altoSyncIntentionsData.filter(
        syncData =>
          syncData.syncStatus !== IntegrationEntitySyncStatusEnum.SYNCED
      );

      if (notSyncedArray.length > 0) {
        return (
          <Message status={AlertStatusEnum.WARNING}>
            <Box>
              {`${notSyncedArray.length} of your intentions are not syncing to LeadPro`}
            </Box>
          </Message>
        );
      }

      return null;
    }, [altoSyncIntentionsData]);

    const handleSyncFilterChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSyncStatusFilter(e.target.checked);
      },
      [setSyncStatusFilter]
    );

    return (
      <IntegrationConfiguratorLayout>
        <Box fontSize={"xl"}>Lead intention mappings</Box>
        {banner}
        <TableV2<TAltoIntentionRelationExtendedData>
          globalFilterInputPlaceholder={"Search intentions by name"}
          additionalActions={
            <SyncStatusFilter
              isChecked={syncStatusFilter}
              onChange={handleSyncFilterChange}
            />
          }
          dataSource={dataSource}
          columns={columnsDef}
          pageSize={DEFAULT_PAGE_SIZE}
        />
        <Box width={"100%"} textAlign={"right"}>
          <Button
            colorScheme={"blue"}
            onClick={submitSyncPairs}
            isLoading={isSubmitting}
          >
            Update
          </Button>
        </Box>
      </IntegrationConfiguratorLayout>
    );
  }
);
